import React, { FormEvent, useCallback, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useFetch } from 'use-http';
import { push, replace } from 'connected-react-router';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import IndexReferenceType from 'src/models/IndexReferenceType';
// import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
import RHFToggle from 'src/components/controls/tempRHFControls/RHFToggle';
import ContractHoldDocs from '../../LeasingComponents/redesign/ContractHoldDocs';
import actions from 'src/actions';
import { DialogContext, dialogCreators } from 'src/providers/DialogProvider';
import DialogList from 'src/dialogs';
import AddWIOwnershipData from 'src/models/tract_detail/add_ownership_revised/AddWIOwnership';
import ContractWorkingInterestList from '../../LeasingComponents/redesign/ContractWorkingInterestList';
import PropertyUtil from 'src/util/PropertyUtil';
import Owner from 'src/models/tract_detail/Owner';
import WIOwnership from 'src/models/tract_detail/WIOwnership';
import Ownership from 'src/models/tract_detail/Ownership';
import WIContractOwnership from 'src/models/tract_detail/WIContractOwnership';
import RHFTextArea from 'src/components/controls/tempRHFControls/RHFTextArea';
import RHFCheckbox from 'src/components/controls/tempRHFControls/RHFCheckBox';
// import MathUtil from 'src/util/MathUtil';
import ProvisionLeaseClause from 'src/models/ProvisionLeaseClause';
import AgreementUtil from 'src/util/AgreementUtil';
import { IRootState } from 'src/reducers';
import AddOwnershipPages from '../AddOwnershipPages';
import OwnerUtil from 'src/util/OwnerUtil';
import ProvisionClauseType from 'src/models/ProvisionClauseType';
import LandContractType from 'src/models/LandContractType';
import ProvisionClause from 'src/components/common/ProvisionClause';
import ProvisioningUpdateDto from 'src/models/ProvisioningUpdateDto';
import RightOfWay, { RightOfWayHold } from 'src/models/contract_types/RightOfWay';
import { rightOfWayLeaseType as rlt } from 'src/components/common/Constants';
import Restriction from 'src/models/leasehold/Restriction';
import AdditionalDamages from 'src/models/AdditionalDamages';
import AdditionalDamagesList from '../../LeasingComponents/AdditionalDamagesList';
import { FormRow } from 'src/components/controls/forms/reactHookForm';
import RestrictionList from '../../LeasingComponents/RestrictionList';
import * as Constants from 'src/components/common/Constants';
import MathUtil from 'src/util/MathUtil';

const COST_APPLIED = [
    { value: 'Up Front', label: 'Up Front' },
    { value: 'Balance', label: 'Balance' },
];

const ROW_PAYMENT_OPTIONS = [
    { value: 'Per rod', label: 'Per rod' },
    { value: 'Per foot', label: 'Per foot' },
    { value: 'Flat fee', label: 'Flat fee' },
];

const filterFields: Array<{ field: string, types: Array<string>}> = [
    { field: 'rowLength', types: [rlt.ROW, rlt.LAG, rlt.DOH, rlt.WAV, rlt.SLA, rlt.AGE, rlt.IL, rlt.PAR, rlt.TAR, rlt.TWS, rlt.VAL, rlt.YRD]},
    { field: 'rowWidth', types: [rlt.ROW, rlt.LAG, rlt.PAR, rlt.TAR]},
    { field: 'tempWidth', types: [rlt.ROW, rlt.LAG, rlt.PAR, rlt.TAR]},
    { field: 'constrDate', types: [rlt.TAR, rlt.TWS]},
    { field: 'noLines', types: [rlt.ROW]},
    { field: 'pipeDiameter', types: [rlt.ROW]},
    { field: 'royalty', types: [rlt.ROW, rlt.LAG, rlt.WAV, rlt.SLA, rlt.AGE, rlt.IL, rlt.PAR, rlt.TAR, rlt.TWS, rlt.VAL, rlt.YRD]},
    { field: 'consideration', types: [rlt.ROW, rlt.LAG, rlt.DOH, rlt.WAV, rlt.SLA, rlt.AGE, rlt.IL, rlt.PAR, rlt.TAR, rlt.TWS, rlt.VAL, rlt.YRD]},
    { field: 'extConsideration', types: [rlt.SLA, rlt.IL, rlt.YRD]},
    { field: 'damages', types: [rlt.ROW, rlt.LAG, rlt.SLA, rlt.AGE, rlt.IL, rlt.PAR, rlt.TAR, rlt.TWS, rlt.VAL, rlt.YRD]},
    { field: 'payTermOrder', types: [rlt.ROW, rlt.LAG, rlt.WAV, rlt.SLA, rlt.AGE, rlt.IL, rlt.PAR, rlt.TAR, rlt.TWS, rlt.VAL, rlt.YRD]},
    { field: 'rental', types: [rlt.ROW]},
    { field: 'recordingInfo', types: [rlt.ROW, rlt.SLA, rlt.AGE, rlt.IL, rlt.PAR, rlt.VAL, rlt.YRD]},
    { field: 'addlDamages', types: [rlt.ROW, rlt.SLA, rlt.AGE, rlt.IL, rlt.PAR, rlt.TAR, rlt.TWS, rlt.VAL, rlt.YRD]}
];

const rTypes = Object.entries(Constants.rightOfWayLeaseType).map((k) => {
    return { value: k[1], label: `${k[0]} - ${k[1]}` };
});

const RightOfWayInfo = () => {
    const appContext = useContext<IAppContextState>(AppContext);
    const { register, handleSubmit, reset, errors, formState, getValues, setValue, watch } = useForm();
    const { get, put, post, response, del } = useFetch();
    const dialogContext = React.useContext<dialogCreators>(DialogContext);

    const dispatch = useDispatch();

    // REDUCERS
    const { project } = useSelector((state: IRootState) => state);

    const [rightOfWayData, setRightOfWayData] = useState<RightOfWay>(undefined);
    const [provisionClauseTypes, setProvisionClauseTypes] = useState<Array<ProvisionClauseType>>(undefined);
    const [measurementTypes, setMeasurementTypes] = useState<Array<IndexReferenceType>>(undefined);
    const [rowLengths, setRowLengths] = useState<Array<IndexReferenceType>>([]);
    const [diameterLengths, setDiameterLengths] = useState<Array<IndexReferenceType>>([]);
    const [attachedProvisions, setAttachedProvisions] = useState<Array<ProvisionLeaseClause>>(undefined);
    const [currentLeaseHold, setCurrentLeaseHold] = useState(undefined);
    const [selectedWIOwnershipId, setSelectedWIOwnershipId] = useState(0);
    const [dataLoaded, setDataLoaded] = useState<boolean>(false);
    const [copyLeaseholdClicked, setCopyLeaseholdClicked] = useState<boolean>(false);
    const [sourceSelected, setSourceSelected] = useState<boolean>(false);
    const [isReadonly, setIsReadonly] = useState<boolean>(false);
    const [contractLookupId, setContractLookupId] = useState<number>(0);
    const [additionalDamages, setAdditionalDamages] = useState<Array<AdditionalDamages>>([]);
    const [restrictions, setRestrictions] = useState<Array<Restriction>>([]);
    const [isPerpetual, setIsPerpetual] = useState<boolean>(false);
    const [isRental, setIsRental] = useState<boolean>(false);
    // Total Costs
    const [considerationTotal, setConsiderationTotal] = useState<number>(0);
    const [initialDamages, setInitialDamages] = useState<number>(0);
    const [upFrontDamages, setUpFrontDamages] = useState<number>(0);
    const [balanceDamages, setBalanceDamages] = useState<number>(0);
    const [totalCosts, setTotalCosts] = useState<number>(0);
    // right of way lease type
    const [leaseType, setLeaseType] = useState<string>(rlt.ROW);

    const { isDirty } = formState;

    useEffect(() => {
        getMeasurementTypes();
        getProvisionClauseTypes();
        if (appContext.rowId > 0) {
            getRightOfWayData(appContext.rowId);
        } else {
            setIsReadonly(false);
        }
    }, []);

    useEffect(() => {
        if (measurementTypes?.length > 0) {
            let rlTypes = measurementTypes.filter(rl => rl.name === 'Feet' || rl.name === 'Rods');
            setRowLengths(rlTypes);
            let diamTypes = measurementTypes.filter(dt => dt.name === 'Inches' || dt.name === 'Feet');
            setDiameterLengths(diamTypes);
        }
    }, [measurementTypes]);

    useEffect(() => {
        setDataLoaded(!!measurementTypes && !!attachedProvisions);
    }, [measurementTypes, attachedProvisions]);

    useEffect(() => {
        if (dataLoaded === true) {
            reset(prepData(rightOfWayData, attachedProvisions));
        }
    }, [dataLoaded]);

    useEffect(() => {
        if (rightOfWayData?.id > 0) {
            if (appContext.rowholdId > 0 && rightOfWayData?.rightOfWayHolds?.findIndex((slh) => slh.id === appContext.rowholdId) > -1) {
                const leaseHoldIndex = rightOfWayData?.rightOfWayHolds?.findIndex((slh) => slh.id === appContext.rowholdId);
                setCurrentLeaseHold(leaseHoldIndex);
            } else if (appContext.rowholdId === 0) {
                const tempRightOfWayData = rightOfWayData;
                tempRightOfWayData.rightOfWayHolds.push(new RightOfWayHold());
                setRightOfWayData(tempRightOfWayData);
                setCurrentLeaseHold(tempRightOfWayData?.rightOfWayHolds?.length - 1);
                setAttachedProvisions([]);
            }
            setLeaseType(rightOfWayData.rowAgreementType ?? rlt.ROW);
        }
    }, [rightOfWayData]);

    useEffect(() => {
        const id = rightOfWayData?.rightOfWayHolds?.[currentLeaseHold]?.contractLookupId;
        if (id > 0) {
            setContractLookupId(id);
        }
    }, [currentLeaseHold]);

    useEffect(() => {
        if (contractLookupId > 0) {
            refreshProvisions();
        }
    }, [contractLookupId]);

    useEffect(() => {
        let formValues = getValues();
        if (attachedProvisions !== undefined) {
            formValues.provisionLeaseClauses = initialProvisions(attachedProvisions);
        }
        reset(formValues);
    }, [attachedProvisions]);

    // Disable Copy Leasehold button until Source Leasehold is selected
    useEffect(() => {
        setSourceSelected(false);
    }, [copyLeaseholdClicked]);

    useEffect(() => {
        if (rightOfWayData !== undefined) {
            handleTotals(rightOfWayData);
        }
    }, [rightOfWayData]);

    const initializePage = (rowId: number = undefined) => {
        setDataLoaded(false);
        setRightOfWayData(undefined);
        setAttachedProvisions(undefined);
        setContractLookupId(undefined);
        setCurrentLeaseHold(undefined);
        getMeasurementTypes();
        getProvisionClauseTypes();
        getRightOfWayData(rowId ?? appContext.rowId);
    };

    // Enable Copy Leasehold button when the selected Source changes from default
    const handleSourceSelected = (e: FormEvent<HTMLSelectElement>) => {
        const selectedItem = e.target as HTMLSelectElement;

        if (selectedItem.value !== '-- Select --') {
            setSourceSelected(true);
            setCopyLeaseholdClicked(true);
        }
    };

    const prepData = useCallback(
        (
            roughLeaseData: RightOfWay,
            attachedProvisionClauses: ProvisionLeaseClause[]
        ) => {
            const formData: RightOfWay = {
                ...roughLeaseData,
                reminderDate: roughLeaseData.reminderDate ? (moment(roughLeaseData.reminderDate).format('YYYY-MM-DD') as unknown as Date) : null,
                createdDate: roughLeaseData.createdDate ? (moment(roughLeaseData.createdDate).format('YYYY-MM-DD') as unknown as Date) : null,
                leaseDate: roughLeaseData.leaseDate ? (moment(roughLeaseData.leaseDate).format('YYYY-MM-DD') as unknown as Date)
                    : roughLeaseData.createdDate ? (moment(roughLeaseData.createdDate).format('YYYY-MM-DD') as unknown as Date) : null,
                effectiveDate: roughLeaseData.effectiveDate ? (moment(roughLeaseData.effectiveDate).format('YYYY-MM-DD') as unknown as Date) : null,
                commitDate: roughLeaseData.commitDate ? (moment(roughLeaseData.commitDate).format('YYYY-MM-DD') as unknown as Date) : null,
                inHandDate: roughLeaseData.inHandDate ? (moment(roughLeaseData.inHandDate).format('YYYY-MM-DD') as unknown as Date) : null,
                originalInHandDate: roughLeaseData.originalInHandDate ? (moment(roughLeaseData.originalInHandDate).format('YYYY-MM-DD') as unknown as Date) : null,
                voidDate: roughLeaseData.voidDate ? (moment(roughLeaseData.voidDate).format('YYYY-MM-DD') as unknown as Date) : null,
                recordDate: roughLeaseData.recordDate ? (moment(roughLeaseData.recordDate).format('YYYY-MM-DD') as unknown as Date) : null,

                currentHold: {
                    ...roughLeaseData.rightOfWayHolds?.[currentLeaseHold],
                }
            };

            if (formData.currentHold.restrictions?.length > 0) {
                for (let res of formData.currentHold.restrictions) {
                    res.createdDate = res.createdDate
                        ? (moment(res.createdDate).format('YYYY-MM-DD') as unknown as Date)
                        : null;
                    res.modifiedDate = res.modifiedDate
                        ? (moment(res.modifiedDate).format('YYYY-MM-DD') as unknown as Date)
                        : null;
                }
            }
            setRestrictions(formData.currentHold.restrictions);

            setAdditionalDamages(formData.currentHold.additionalDamages);

            for (let provision of attachedProvisionClauses) {
                provision.obligationDate = provision.obligationDate ? (moment(provision.obligationDate).format('YYYY-MM-DD') as unknown as Date) : null;
                provision.obligationEndDate = provision.obligationEndDate ? (moment(provision.obligationEndDate).format('YYYY-MM-DD') as unknown as Date) : null;
            }
            formData.currentHold.provisionLeaseClauses = attachedProvisionClauses;

            if (formData.isGeneratedLease === true && formData.isInHand === true) {
                setIsReadonly(true);
            } else {
                setIsReadonly(false);
            }

            setIsPerpetual(formData.isPerpetual === true);
            setIsRental(formData.isRental === true);

            return formData;
        },
        [currentLeaseHold, attachedProvisions, measurementTypes]
    );

    const initialProvisions = (data: ProvisionLeaseClause[]) => {
        for (let i = 0; i < data.length; i++) {
            data[i].obligationDate = data[i].obligationDate
                ? (moment(data[i].obligationDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data[i].obligationEndDate = data[i].obligationEndDate
                ? (moment(data[i].obligationEndDate).format('YYYY-MM-DD') as unknown as Date) : null;
        }
        return data;
    };

    const getRightOfWayData = async (rowId: number) => {
        const result: RightOfWay = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/rightOfWay/${rowId}`
        );
        if (response.ok) {
            setRightOfWayData(result);
        } else {
            window.alert('Get Right Of Way Data failed');
        }
    };

    const getMeasurementTypes = async () => {
        const result = await get(`/v1.0/referenceTypes`);

        if (response.ok) {
            setMeasurementTypes(result);
        }
    };

    const getProvisionClauseTypes = async () => {
        const result: Array<ProvisionClauseType> = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/provisionLeaseClause/types/${LandContractType.RightOfWay}`);

        if (response.ok && Array.isArray(result)) {
            setProvisionClauseTypes(result ?? []);
        }
    };

    const refreshProvisions = async () => {
        const result: Array<ProvisionLeaseClause> = await get(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/provisionLeaseClause/agreements/${contractLookupId}`
        );
        if (response.ok) {
            setAttachedProvisions(result);
        }
    };

    const updateProvisionData = async (provisions: ProvisionLeaseClause[]) => {
        let attachedIds = provisions.map(plc => plc.id);
        const provisionDto: ProvisioningUpdateDto = {
            provisionLeaseClauses: provisions,
            provisionDeletes: (attachedProvisions ?? []).map(ap => ap.id).filter(ap => attachedIds.indexOf(ap) === -1),
            contractLookupIds: [contractLookupId]
        };
        await put(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/provisionLeaseClause`, provisionDto);

        if (!response.ok) {
            window.alert('Unable to Update Provisions');
        }
    };

    const updateRightOfWay = async (data: RightOfWay) => {
        let currentPath = window.location.pathname.split('/');
        currentPath.splice(currentPath.length - 3, 3);

        let result = await put(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/rightOfWay`,
            data
        );

        if (response.ok) {
            initializePage();
            if (appContext.rowholdId > 0) {
                dispatch(
                    push(window.location.pathname)
                );

            } else {
                dispatch(
                    replace(
                        `${currentPath.join('/')}/${result.contractId}/right-of-way-holds/${result.contractHoldId}`
                    )
                );
            }
        } else {
            window.alert('Update Right Of Way failed');
        }
    };

    const deleteRightOfWayHold = (deleteParent: boolean) => {
        const confirmAction = async () => {
            const deleteId = rightOfWayData?.rightOfWayHolds?.[currentLeaseHold].id;
            let currentPath = window.location.pathname.split('/');
            let spliceLength = deleteParent === true ? 5 : 1;
            currentPath.splice(currentPath.length - spliceLength);
            
            // tslint:disable-next-line: max-line-length
            await del(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/rightOfWay/${deleteId}/${deleteParent}`);
            if (response.ok) {
                if (!deleteParent) {
                    const switchToLeaseholdId = rightOfWayData?.rightOfWayHolds.find((x) => x.id !== rightOfWayData?.rightOfWayHolds?.[0]?.id)?.id;
                    getRightOfWayData(appContext.rowId);
                    dispatch(
                        replace(
                            `${currentPath.join('/')}/${switchToLeaseholdId}`
                        )
                    );
                } else {
                    dispatch(
                        replace(
                            `${currentPath.join('/')}`
                        )
                    );
                }
            
            } else {
                window.alert('delete leasehold failed');
            }
        };

        let warning = deleteParent
            ? 'This is the only leasehold. The lease will also be deleted. Continue?'
            : 'Are you sure you want to delete this Leasehold?';

        const body = (
            <>
                <div style={{ fontWeight: 'bold' }}>{warning}</div>
            </>
        );

        dialogContext.common.confirmAction(`Delete Leasehold`, body, confirmAction);
    };

    const createRightOfWay = async (data: RightOfWay) => {
        let currentPath = window.location.pathname.split('/');
        currentPath.splice(currentPath.length - 3, 3);

        let result = await post(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/rightOfWay`,
            data
        );
        if (response.ok) {
            dispatch(
                replace(
                    `${currentPath.join('/')}/${result.contractId}/right-of-way-holds/${result.contractHoldId}`
                )
            );
            initializePage(result.contractId);
        } else {
            window.alert('Create Right Of Way failed');
        }
    };

    const onSubmit = async (data: RightOfWay) => {
        data = finalizeRightOfWay(data);
        if (data.id > 0) {
            if (contractLookupId > 0 && data.currentHold !== undefined) {
                await updateProvisionData(data.currentHold.provisionLeaseClauses);
            }
            updateRightOfWay(data);
        } else {
            data.isInHand = true;
            data.inHandDate = new Date();
            data.originalInHandDate = new Date();
            data.isCommitted = true;
            data.commitDate = new Date();
            createRightOfWay(data);
        }
    };

    const finalizeRightOfWay = (data: RightOfWay) => {
        data.id = rightOfWayData?.id ? parseInt(rightOfWayData?.id + '', 10) : 0;
        data.invoiceableLookupId = rightOfWayData?.invoiceableLookupId ?? null;
        data.rowNumber = rightOfWayData?.rowNumber ? rightOfWayData?.rowNumber : 0;
        data.rteLegalDescription = rightOfWayData?.rteLegalDescription ? rightOfWayData.rteLegalDescription : '';
        data.clientDocNumber = data.clientDocNumber ? data.clientDocNumber : '';
        data.leasedBy = data.leasedBy ? data.leasedBy : '';
        data.lesseeName = data.lesseeName ? data.lesseeName : '';
        data.leaseLessorNotes = data.leaseLessorNotes ? data.leaseLessorNotes : '';
        data.legalDescription = data.legalDescription ? data.legalDescription : '';
        data.volume = data.volume ? data.volume : '';
        data.page = data.page ? data.page : '';
        data.instrumentNumber = data.instrumentNumber ? data.instrumentNumber : '';
        data.originalLessor = data.originalLessor ? data.originalLessor : '';
        data.lessorAka = data.lessorAka ? data.lessorAka : '';
        data.originalLessee = data.originalLessee ? data.originalLessee : '';
        data.leasedBy = data.leasedBy ? data.leasedBy : '';
        data.ownersOnDocument = rightOfWayData?.ownersOnDocument || '';
        data.lesseeName = rightOfWayData?.lesseeName || '';
        // TODO: Replace this with Total Bonus Calc once it's ready
        data.bonusAmount = rightOfWayData?.bonusAmount ? parseFloat(rightOfWayData.bonusAmount + '') : 0;

        data.reminderDate = data.reminderDate
            ? (moment(data.reminderDate).format('YYYY-MM-DD') as unknown as Date)
            : (moment().format('YYYY-MM-DD') as unknown as Date);
        data.createdDate = data.createdDate
            ? (moment(data.createdDate).format('YYYY-MM-DD') as unknown as Date)
            : (moment().format('YYYY-MM-DD') as unknown as Date);
        data.effectiveDate = data.effectiveDate
            ? (moment(data.effectiveDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.commitDate = data.commitDate
            ? (moment(data.commitDate).format('YYYY-MM-DD') as unknown as Date)
            : (moment().format('YYYY-MM-DD') as unknown as Date);
        data.inHandDate = data.inHandDate
            ? (moment(data.inHandDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.originalInHandDate = data.originalInHandDate
            ? (moment(data.originalInHandDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.leaseDate = data.leaseDate
            ? (moment(data.leaseDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.recordDate = data.recordDate
            ? (moment(data.recordDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.constraintDate = data.constraintDate
            ? (moment(data.constraintDate).format('YYYY-MM-DD') as unknown as Date)
            : null;

        data.modifiedByUserId = data.modifiedByUserId ? parseInt(data.modifiedByUserId + '', 10) : null;
        data.bonusTypeId = data.bonusTypeId ? parseInt(data.bonusTypeId + '', 10) : null;
        data.numberOfLines = data.numberOfLines ? parseInt(data.numberOfLines + '', 10) : null;
        data.pipeLineSizeTypeId = data.pipeLineSizeTypeId ? parseInt(data.pipeLineSizeTypeId + '', 10) : null;
        data.notExceedingTypeId = data.notExceedingTypeId ? parseInt(data.notExceedingTypeId + '', 10) : null;
        data.rowLengthTypeId = data.rowLengthTypeId ? parseInt(data.rowLengthTypeId + '', 10) : null;
        data.rowWidthTypeId = data.rowWidthTypeId ? parseInt(data.rowWidthTypeId + '', 10) : null;
        data.tempWidthTypeId = data.tempWidthTypeId ? parseInt(data.tempWidthTypeId + '', 10) : null;
        data.recordTypeId = data.recordTypeId ? parseInt(data.recordTypeId + '', 10) : null;
        data.payTermOrder = data.payTermOrder ? parseInt(data.payTermOrder + '', 10) : null;
        data.payTermTypeId = data.payTermTypeId ? parseInt(data.payTermTypeId + '', 10) : null;

        data.originalGrossAcres = data.originalGrossAcres ? parseFloat(data.originalGrossAcres + '') : null;
        data.consideration = data.consideration ? parseFloat(data.consideration + '') : null;
        data.extraConsideration = data.extraConsideration ? parseFloat(data.extraConsideration + '') : null;
        data.damages = data.damages ? parseFloat(data.damages + '') : null;
        data.pipeLineSize = data.pipeLineSize ? parseFloat(data.pipeLineSize + '') : null;
        data.pipeLineNotExceeding = data.pipeLineNotExceeding ? parseFloat(data.pipeLineNotExceeding + '') : null;
        data.rowLength = data.rowLength ? parseFloat(data.rowLength + '') : null;
        data.rowWidth = data.rowWidth ? parseFloat(data.rowWidth + '') : null;
        data.tempWidth = data.tempWidth ? parseFloat(data.tempWidth + '') : null;
        data.rentalAmount = data.rentalAmount ? parseFloat(data.rentalAmount + '') : null;
        data.rentalEscalatingPercentage = data.rentalEscalatingPercentage ? parseFloat(data.rentalEscalatingPercentage + '') : null;

        data.isFiledByMemo = AgreementUtil.parseBool(data.isFiledByMemo + '');
        data.damagesPaymentUpFront = AgreementUtil.parseBool(data.damagesPaymentUpFront + '');
        data.isPerpetual = AgreementUtil.parseBool(data.isPerpetual + '');
        data.isRental = AgreementUtil.parseBool(data.isRental + '');

        if (!!rightOfWayData) {
            data.reminderNote = rightOfWayData?.reminderNote ? rightOfWayData.reminderNote : '';
            data.lesseeAddressLine1 = rightOfWayData?.lesseeAddressLine1 ? rightOfWayData.lesseeAddressLine1 : '';
            data.lesseeAddressLine2 = rightOfWayData?.lesseeAddressLine2 ? rightOfWayData.lesseeAddressLine2 : '';
            data.lesseeCity = rightOfWayData?.lesseeCity ? rightOfWayData.lesseeCity : '';
            data.lesseeState = rightOfWayData?.lesseeState ? rightOfWayData.lesseeState : '';
            data.lesseeZipCode = rightOfWayData?.lesseeZipCode ? rightOfWayData.lesseeZipCode : '';
            data.ownersAddressOnDocument = rightOfWayData?.ownersAddressOnDocument ? rightOfWayData.ownersAddressOnDocument : '';
            data.ownersSSNTaxIdOnDocument = rightOfWayData?.ownersSSNTaxIdOnDocument ? rightOfWayData.ownersSSNTaxIdOnDocument : '';
            data.ownersOnDocument = rightOfWayData.ownersOnDocument ? rightOfWayData.ownersOnDocument : '';
            data.lesseeName = rightOfWayData.lesseeName ? rightOfWayData.lesseeName : '';
            data.lesseeOwnerLookupId = rightOfWayData?.lesseeOwnerLookupId ? parseInt(rightOfWayData?.lesseeOwnerLookupId + '', 10) : null;

            data.isNotOriginalLessee = rightOfWayData?.isNotOriginalLessee ? rightOfWayData.isNotOriginalLessee : null;
            data.isGeneratedLease = rightOfWayData?.isGeneratedLease ? rightOfWayData.isGeneratedLease : false;
            data.isVoid = rightOfWayData?.isVoid ? rightOfWayData.isVoid : false;
            data.isInHand = rightOfWayData?.isInHand ? rightOfWayData.isInHand : false;
            data.isCommitted = rightOfWayData?.isCommitted ? rightOfWayData.isCommitted : false;

            data.voidDate = rightOfWayData?.voidDate ? (moment(rightOfWayData.voidDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.commitDate = rightOfWayData?.commitDate ? (moment(rightOfWayData.commitDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.inHandDate = rightOfWayData?.inHandDate ? (moment(rightOfWayData.inHandDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.originalInHandDate = rightOfWayData?.originalInHandDate ? (moment(rightOfWayData.originalInHandDate).format('YYYY-MM-DD') as unknown as Date) : null;
            data.reminderDate = rightOfWayData?.reminderDate ? (moment(rightOfWayData.reminderDate).format('YYYY-MM-DD') as unknown as Date) : null;
        }

        if (data.currentHold !== undefined) {
            let formatted = new Array<RightOfWayHold>();
            formatted.push(finalizeRightOfWayHold(data.currentHold));
            data.rightOfWayHolds = [...formatted];
        }

        return data;
    };

    const finalizeRightOfWayHold = (data: RightOfWayHold) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.projectId = project.currentProject?.id;
        data.rightOfWayId = rightOfWayData?.id ? rightOfWayData.id : 0;
        data.contractLookupId = rightOfWayData?.rightOfWayHolds?.[0]?.contractLookupId ? rightOfWayData.rightOfWayHolds[0].contractLookupId : 0;
        data.rowHoldNumber = data.rowHoldNumber ? parseInt(data.rowHoldNumber + '', 10) : null;

        data.primaryTerm = data.primaryTerm ? parseInt(data.primaryTerm + '', 10) : null;
        data.primaryTermTypeId = data.primaryTermTypeId ? parseInt(data.primaryTermTypeId + '', 10) : null;
        data.extensionTerm = data.extensionTerm ? parseInt(data.extensionTerm + '', 10) : null;
        data.extensionTermTypeId = data.extensionTermTypeId ? parseInt(data.extensionTermTypeId + '', 10) : null;
        data.agentId = data.agentId ? parseInt(data.agentId + '', 10) : null;

        data.royalty = data.royalty ? parseFloat(data.royalty + '') : null;

        data.isActive = data.isActive ? data.isActive : null;
        data.isHistorical = data.isHistorical ? data.isHistorical : null;

        if (data.contractOwnerships?.length > 0) {
            let formatted = new Array<WIContractOwnership>();
            data.contractOwnerships.forEach((owner: WIContractOwnership) => {
                formatted.push(finalizeWIOwnership(owner));
            });
            data.contractOwnerships = [...formatted];
        }

        if (data.provisionLeaseClauses?.length > 0) {
            data.provisionLeaseClauses = data.provisionLeaseClauses.map((plc) => {
                plc.id = plc.id ? parseInt(plc.id + '', 10) : 0;
                plc.contractLookupId = rightOfWayData?.rightOfWayHolds[currentLeaseHold]?.contractLookupId ?? null;
                plc.invoiceableLookupId = plc.invoiceableLookupId ? parseInt(plc.invoiceableLookupId + '', 10) : null;
                plc.contractAdHocClauseId = plc.contractAdHocClauseId ? parseInt(plc.contractAdHocClauseId + '', 10) : null;
                plc.leaseAdHocClauseId = plc.leaseAdHocClauseId ? parseInt(plc.leaseAdHocClauseId + '', 10) : null;
                plc.contractAddendumClauseId = plc.contractAddendumClauseId ? parseInt(plc.contractAddendumClauseId + '', 10) : null;
                plc.clauseTypeId = plc.clauseTypeId ? parseInt(plc.clauseTypeId + '', 10) : null;
                plc.frequency = plc.frequency ? parseInt(plc.frequency + '', 10) : null;
                plc.obligationDate = plc.obligationDate ? (moment(plc.obligationDate).format('YYYY-MM-DD') as unknown as Date) : null;
                plc.obligationEndDate = plc.obligationEndDate ? (moment(plc.obligationEndDate).format('YYYY-MM-DD') as unknown as Date) : null;
                plc.useClientApproved = plc.useClientApproved ? AgreementUtil.parseBool(plc.useClientApproved + '') : null;
                return plc;
            });
        } else {
            data.provisionLeaseClauses = data.provisionLeaseClauses ?? [];
        }

        data.additionalDamages = data.additionalDamages?.length > 0 ? finalizeDamages(data.additionalDamages) : [];
        data.restrictions = data.restrictions?.length > 0 ? finalizeRestrictions(data.restrictions) : [];

        return data;
    };

    const finalizeWIOwnership = (data: WIContractOwnership) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.projectId = data.projectId ? parseInt(data.projectId + '', 10) : 0;
        data.ownerLookUpId = data.ownerLookUpId ? parseInt(data.ownerLookUpId + '', 10) : 0;
        data.parentOwnershipId = data.parentOwnershipId ? parseInt(data.parentOwnershipId + '', 10) : 0;
        data.ownershipId = data.ownershipId ? parseInt(data.ownershipId + '', 10) : 0;
        data.contractLookupId = data.contractLookupId ? parseInt(data.contractLookupId + '', 10) : 0;

        data.decimalInterest = data.decimalInterest ? parseFloat(data.decimalInterest + '') : 0;

        data.note = data.note ? data.note : '';

        if (data.orriOwnerships?.length > 0) {
            let formatted = new Array<WIContractOwnership>();
            data.orriOwnerships.forEach((orri: WIContractOwnership) => {
                formatted.push(finalizeWIOwnership(orri));
            });
            data.orriOwnerships = [...formatted];
        }

        return data;
    };

    const finalizeDamages = (data: AdditionalDamages[]) => {
        for (let dmg of data) {
            dmg.createdByUserId = dmg.createdByUserId ? parseInt(dmg.createdByUserId + '', 10) : null;
            dmg.agreementTypeId = dmg.agreementTypeId ? parseInt(dmg.agreementTypeId + '', 10) : null;
            dmg.modifiedByUserId = dmg.modifiedByUserId ? parseInt(dmg.modifiedByUserId + '', 10) : null;
            dmg.contractLookupId = dmg.contractLookupId ? parseInt(dmg.contractLookupId + '', 10) : null;
            dmg.id = dmg.id ? parseInt(dmg.id + '', 10) : 0;

            dmg.cost = dmg.cost ? parseFloat(dmg.cost + '') : null;
            dmg.totalCost = dmg.totalCost ? parseFloat(dmg.totalCost + '') : null;

            dmg.createdDate = dmg.createdDate ? (moment(dmg.createdDate).format('YYYY-MM-DD') as unknown as Date) : null;
            dmg.modifiedDate = dmg.modifiedDate ? (moment(dmg.modifiedDate).format('YYYY-MM-DD') as unknown as Date) : null;
        }

        return data;
    };

    const finalizeRestrictions = (data: Restriction[]) => {
        for (let res of data) {
            res.id = res.id ? parseInt(res.id + '', 10) : 0;
            res.no = res.no ? parseInt(res.no + '', 10) : null;
            res.createdByUserId = res.createdByUserId ? parseInt(res.createdByUserId + '', 10) : null;
            res.modifiedByUserId = res.modifiedByUserId ? parseInt(res.modifiedByUserId + '', 10) : null;
            res.contractLookupId = res.contractLookupId ? parseInt(res.contractLookupId + '', 10) : null;

            res.createdDate = res.createdDate ? (moment(res.createdDate).format('YYYY-MM-DD') as unknown as Date) : null;
            res.modifiedDate = res.modifiedDate ? (moment(res.modifiedDate).format('YYYY-MM-DD') as unknown as Date) : null;
        }

        return data;
    };

    const previousContractHold = () => {
        if (currentLeaseHold > 0) {
            const rowholdId = rightOfWayData?.rightOfWayHolds?.[currentLeaseHold - 1]?.id;
            setCurrentLeaseHold(currentLeaseHold - 1);
            dispatch(
                push(
                    `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.fieldNumber}/surface-tracts/${appContext.surfaceTractNumber}/ownership/${appContext.ownershipId}/right-of-ways/${appContext.rowId}/right-of-way-holds/${rowholdId}`
                )
            );
        }
    };

    const nextContractHold = () => {
        if (currentLeaseHold + 1 < rightOfWayData?.rightOfWayHolds?.length) {
            const rowHoldId = rightOfWayData?.rightOfWayHolds?.[currentLeaseHold + 1]?.id;

            setCurrentLeaseHold(currentLeaseHold + 1);
            dispatch(
                push(
                    `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.fieldNumber}/surface-tracts/${appContext.surfaceTractNumber}/ownership/${appContext.ownershipId}/right-of-ways/${appContext.rowId}/right-of-way-holds/${rowHoldId}`
                )
            );
        }
    };

    // const calculateTotal = (
    //     measurementTypeId: number,
    //     paymentAmount: number,
    //     grossAcres: number,
    //     measurementTypesList: Array<IndexReferenceType>
    // ) => {
    //     let measurementType = measurementTypesList.find((mt) => mt.id === measurementTypeId)?.name;

    //     return measurementType !== undefined ? Number(MathUtil.cleanDollarAmount(measurementType === 'Per Item' ? paymentAmount * grossAcres : paymentAmount)) : 0;
    // };

    const onSaveAddOwnership = async (values: AddWIOwnershipData) => {
        values.contractOwnership.contractLookupId = rightOfWayData.rightOfWayHolds[currentLeaseHold].contractLookupId;

        await post(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/contract-ownership/${LandContractType.RightOfWay}`,
            values
        );

        if (response.ok) {
            getRightOfWayData(appContext.rowId);
        } else {
            window.alert('Update Contract Ownership failed');
        }
    };

    const onTryAddWI = () => {
        if (!isNaN(rightOfWayData?.rightOfWayHolds?.[currentLeaseHold]?.id)) {
            dialogContext.contract.addContractOwnership(
                DialogList.ADD_WI_OWNERSHIP,
                'Add Working Interest Ownership',
                undefined,
                undefined,
                appContext.clientNumber,
                appContext.projectNumber,
                AddOwnershipPages.AddOwnershipLandingPage,
                (path: string) => dispatch(push(path)),
                null,
                onSaveAddOwnership
            );
        } else {
            dispatch(actions.layout.showDialog(DialogList.LEASEHOLD_SAVE_BEFORE_ADDING_WI));
        }
    };

    const onAddORRI = (newParentOwnershipId: number) => {
        dialogContext.contract.addContractOwnership(
            DialogList.ADD_ORRI_OWNERSHIP,
            'Add Overriding Royalty Interest Ownership',
            undefined,
            newParentOwnershipId,
            appContext.clientNumber,
            appContext.projectNumber,
            AddOwnershipPages.AddOwnershipLandingPage,
            (path: string) => dispatch(push(path)),
            null,
            onSaveAddOwnership
        );
    };

    const onCopyORRI = (ownershipId: number) => {
        dialogContext.leasehold.cloneORRI(ownershipId, ownerNameForWorkingInterest(ownershipId), () => location.reload());
    };

    const ownerNameForWorkingInterest = (workingInterestId: number) => {
        const wiOwners = PropertyUtil.SafeGet(() => rightOfWayData?.rightOfWayHolds?.[currentLeaseHold]?.wiOwners);
        const wiOwnerships = PropertyUtil.SafeGet(() => rightOfWayData?.rightOfWayHolds?.[currentLeaseHold]?.contractOwnerships);
        const ownership = wiOwnerships?.filter((wio: Ownership) => wio.id === workingInterestId)[0];
        const owner = wiOwners.filter((o: Owner) => o.ownerLookUpId === ownership.ownerLookUpId)[0];

        return owner?.name || '[Error]';
    };

    const onDeleteORRI = (ownershipId: number) => {
        const wiOwners = PropertyUtil.SafeGet(() => rightOfWayData?.rightOfWayHolds?.[currentLeaseHold]?.wiOwners);
        const wiOwnerships = PropertyUtil.SafeGet(() => rightOfWayData?.rightOfWayHolds?.[currentLeaseHold]?.contractOwnerships);
        const ownership: WIOwnership = wiOwnerships?.filter((wio: WIOwnership) => wio.id === ownershipId)[0];

        dialogContext.leasehold.deleteORRI(ownershipId, ownerNameForWorkingInterest(ownershipId), ownership.orriOwnerships, wiOwners, () =>
            location.reload()
        );
    };

    const onDeleteContractOwnership = async (contractOwnershipId: number) => {
        await del(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/contract-ownership/${contractOwnershipId}`
        );
        if (response.ok) {
            dispatch(actions.layout.hideDialog());
            getRightOfWayData(appContext?.rowId);
        } else {
            window.alert('Delete Ownership failed.');
        }
    };

    const onUpdateWINote = async (ownership: Ownership) => {
        await put(
            `/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/fields/${appContext.sectionNumber}/surfaceTracts/notes`,
            ownership
        );

        if (response.ok) {
            dispatch(actions.layout.hideDialog());
            getRightOfWayData(appContext.rowId);
        } else {
            window.alert(`update wi note failed. note: ${ownership.note}`);
        }
    };

    const findOwnership = (ownershipId: number = 0) => {
        let refId = ownershipId > 0 ? ownershipId : selectedWIOwnershipId;
        let ownership = rightOfWayData.rightOfWayHolds[currentLeaseHold].contractOwnerships?.find(
            (lo: WIContractOwnership) => lo.id === refId
        );

        if (!ownership) {
            rightOfWayData.rightOfWayHolds[currentLeaseHold].contractOwnerships?.forEach((lo: WIContractOwnership) => {
                let tempOwnership = lo.orriOwnerships.find((wio: WIContractOwnership) => wio.id === refId);
                if (tempOwnership) {
                    ownership = tempOwnership;
                }
            });
        }

        return ownership;
    };

    const openOwnershipNotes = (ownershipId: number) => {
        // this.setState({ ownershipNotesId: ownershipId });
        setSelectedWIOwnershipId(ownershipId);
        dialogContext.tract.openOwnershipNotes(
            findOwnership(ownershipId),
            rightOfWayData?.rightOfWayHolds?.[currentLeaseHold]?.wiOwners,
            '',
            2,
            (ownership: Ownership) => onUpdateWINote(ownership)
        );
    };

    const onCopyLeasehold = async () => {
        setAttachedProvisions(undefined);
        setDataLoaded(false);
        let copyLeaseholdId = getValues('sourceLeaseholdId');

        // tslint:disable-next-line: max-line-length
        const result = await put(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/rightOfWay/row-hold/${appContext.rowholdId}/copy-leasehold/${copyLeaseholdId}`);

        if (response.ok) {
            let currentPath = window.location.pathname.split('/');
            currentPath.splice(currentPath.length - 1, 1);

            dispatch(
                replace(
                    `${currentPath.join('/')}/${result}`
                )
            );
            setCopyLeaseholdClicked(false);
            initializePage();
            getRightOfWayData(appContext.rowId);
        } else {
            window.alert('call failed');
        }
    };

    const leaseholdList = () => {
        if (rightOfWayData?.rightOfWayHolds?.length > 1) {
            let holds = [...rightOfWayData.rightOfWayHolds];
            holds.pop();
            let leaseholdIds = holds.map((lh: RightOfWayHold) => {
                return { value: lh.id || 0, label: lh.rowHoldNumber?.toString() || 'Error' };
            });

            return leaseholdIds;
        } else {
            return [];
        }
    };

    const calcRowTotal = (
        measurementType: string,
        paymentAmount: number,
        netLength: number
    ) => {
        const rowFactor = Constants.rodToFeetConversion;
        const lengthType = measurementTypes?.find((mt) => mt.id === getValues().rowLengthTypeId)?.name;

        switch (measurementType) {
            case 'Flat fee':
                return Number(MathUtil.cleanDollarAmount(paymentAmount));
            case 'Per rod':
                if (lengthType === 'Feet') {
                    netLength /= rowFactor;
                }
                return Number(MathUtil.cleanDollarAmount(paymentAmount * netLength));
            case 'Per foot':
                if (lengthType === 'Rods') {
                    netLength *= rowFactor;
                }
                return Number(MathUtil.cleanDollarAmount(paymentAmount * netLength));
            default:
                return 0;
        }
    };

    const handleTotals = (rep: RightOfWay) => {
        let netLength = rep.rowLength ? parseFloat(rep.rowLength + '') : 0;

        let allTotals = 0;

        let consideration = parseFloat(rep.consideration + '');
        let cTotal = 0;
        if (!isNaN(consideration) && !!rep.considerationType) {
            cTotal = calcRowTotal(rep.considerationType, consideration, netLength);
        }

        let extraConsideration = parseFloat(rep.extraConsideration + '');
        if (!isNaN(extraConsideration) && !!rep.extraConsiderationType) {
            cTotal += calcRowTotal(rep.extraConsiderationType, extraConsideration, netLength);
        }

        let rentalPrice = parseFloat(rep.rentalAmount + '');
        if (!isNaN(rentalPrice) && !!rep.rentalAmountType) {
            cTotal += calcRowTotal(rep.rentalAmountType, rentalPrice, netLength);
        }

        allTotals += cTotal;
        setConsiderationTotal(cTotal);

        let damages = parseFloat(rep.damages + '');
        if (!isNaN(damages) && !!rep.damagesType) {
            let idTotal = calcRowTotal(rep.damagesType, damages, netLength);
            setInitialDamages(idTotal);
            allTotals += idTotal;
        }

        let addlDamages = rep.currentHold?.additionalDamages as unknown as Array<AdditionalDamages>;

        if (addlDamages?.length > 0) {
            let uDamages = addlDamages.filter(ad => ad.costApplied === COST_APPLIED[0].value);
            let upFrontTotal = 0;

            for (let ufd of uDamages) {
                let upFrontAmount = parseFloat(ufd.cost + '');
                if (!isNaN(upFrontAmount) && !!ufd.costType) {
                    let ufTotal = calcRowTotal(ufd.costType, upFrontAmount, netLength);
                    upFrontTotal += ufTotal;
                }
            }
            setUpFrontDamages(upFrontTotal);

            let bDamages = addlDamages.filter(ad => ad.costApplied === COST_APPLIED[1].value);
            let balanceTotal = 0;

            for (let ufd of bDamages) {
                let upFrontAmount = parseFloat(ufd.cost + '');
                if (!isNaN(upFrontAmount) && !!ufd.costType) {
                    let ufTotal = calcRowTotal(ufd.costType, upFrontAmount, netLength);
                    balanceTotal += ufTotal;
                }
            }
            setBalanceDamages(balanceTotal);

            allTotals += upFrontTotal + balanceTotal;
        }

        setTotalCosts(allTotals);
        setValue('bonusAmount', allTotals);
    };

    const onAddDamages = () => {
        let optionList = [...additionalDamages];
        let newOption = new AdditionalDamages();
        optionList.push(newOption);
        setAdditionalDamages(optionList);
    };
    
    const handleRemoveDamages = (index: number) => {
        let resForm = getValues();
        resForm.currentHold.additionalDamages.splice(index, 1);
        setAdditionalDamages(resForm.currentHold.additionalDamages);
        
        reset(resForm);
        handleTotals(resForm as unknown as RightOfWay);
    };

    const onAddRestrictions = () => {
        let optionList = [...restrictions];
        let newOption = new Restriction();
        optionList.push(newOption);
        setRestrictions(optionList);
    };
    
    const handleRemoveRestrictions = (index: number) => {
        let resForm = getValues();
        resForm.currentHold.restrictions.splice(index, 1);
        setRestrictions(resForm.currentHold.restrictions);
        
        reset(resForm);
    };

    const checkPerpetual = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setIsPerpetual(e.currentTarget.value === 'true');
    };

    const checkRental = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setIsRental(e.currentTarget.value === 'true');
    };

    // By default, fields inside this check will be hidden unless noted in the filterFields const at the top of the page.
    const handleVisibility = (formRow: string) => {
        let arrIndex = filterFields.find(ff => ff.field === formRow);
        if (arrIndex !== undefined) {
            return arrIndex.types.includes(leaseType);
        }
        return false;
    };

    const warningStyle: React.CSSProperties = {
        display: 'inline-block',
        padding: '0.5rem',
        margin: '0 0 1rem 0',
        fontWeight: 700,
        background: '#d5d5d5',
        fontSize: '.8em',
        color: '#565656',
        width: '100%',
        textAlign: 'center',
        borderRadius: '0.4rem',
    };

    return (
        <div className="lease-info-page">
            <div>
                <form onSubmit={handleSubmit((data) => onSubmit(data as unknown as RightOfWay))}>
                    <div>
                        {isDirty && (
                            <>
                                <div
                                    id="dirtyFlag"
                                    style={{
                                        position: 'fixed',
                                        top: '4.7rem',
                                        left: '0',
                                        width: '100%',
                                        color: 'white',
                                        backgroundColor: 'rgba(255,0,0,.5)',
                                        zIndex: 9000,
                                        padding: '1rem 0',
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <div>Unsaved Changes - Please Save Before Leaving Form</div>
                                    <div style={{ position: 'absolute', right: '.5rem', top: '50%', transform: 'translateY(-50%)', display: 'flex' }}>
                                        <div>
                                            <Button text="Save" type="submit" size="small" intent="primary" />
                                        </div>
                                        <div style={{ margin: '0 1rem', borderRight: '1px solid white', width: '1px' }} />
                                        <Button text="Cancel Changes" size="small" onClick={() => reset()} />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Added to let the user know fields with * are required */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.5rem', marginRight: '1rem' }}>
                        <span style={{ color: 'red', transform: 'scale(2)' }}>*</span>
                        <p>= Fields are required!</p>
                    </div>

                    <RHFLegend title="Right Of Way" />
                    <div hidden={true}>
                        <RHFInputBox label="" name="id" register={register} type="number" disabled={true} errors={errors} />
                        <RHFCheckbox name="isGeneratedLease" label="" disabled={true} register={register} errors={errors} />
                        <RHFInputBox
                            label=""
                            name="currentHold.rightOfWayId"
                            register={register}
                            type="number"
                            disabled={true}
                            errors={errors}
                        />
                        <RHFInputBox
                            label=""
                            name="currentHold.projectId"
                            register={register}
                            type="number"
                            disabled={true}
                            errors={errors}
                        />
                        <RHFInputBox
                            label=""
                            name="currentHold.rowHoldNumber"
                            register={register}
                            type="number"
                            disabled={true}
                            errors={errors}
                        />
                        <RHFInputBox
                            label=""
                            name="currentHold.contractLookupId"
                            register={register}
                            type="number"
                            disabled={true}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Bonus Amount"
                            name="bonusAmount"
                            register={register}
                            errors={errors}
                            disabled={true}
                        />
                    </div>
                    <div className="form-row">
                        <RHFSelect
                            name="rowAgreementType"
                            label="Agreement Type"
                            options={rTypes}
                            register={register}
                            errors={errors}
                            disabled={rightOfWayData?.rowAgreementType?.length > 0}
                            onChange={(e) => setLeaseType(e.currentTarget.value)}
                        />
                        <RHFInputBox
                            label="Right Of Way Number"
                            name="rowNumber"
                            style={{ width: '100%' }}
                            register={register}
                            errors={errors}
                            disabled={true}
                        />
                        <RHFInputBox
                            label="Lease Date"
                            name="leaseDate"
                            register={register}
                            type="date"
                            errors={errors}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                            disabled={isReadonly}
                        />
                        <RHFInputBox
                            label="Effective Date"
                            name="effectiveDate"
                            register={register}
                            type="date"
                            errors={errors}
                            rules={{ required: 'field required' }}
                            isRequired={true}
                            disabled={isReadonly}
                        />
                        {handleVisibility('constrDate') && (
                            <RHFInputBox
                                type="date"
                                name="constraintDate"
                                label="Constr Date"
                                register={register}
                                errors={errors}
                                isRequired={true}
                                disabled={isReadonly}
                                bodyStyle={{ width: '33%' }}
                                style={{ width: '100%' }}
                            />
                        )}
                        {handleVisibility('recordingInfo') && (
                            <>
                                <RHFSelect
                                    name="recordTypeId"
                                    label="Record Type"
                                    options={[
                                        { value: 1, label: 'Book/Volume' },
                                        { value: 2, label: 'Instrument #' },
                                    ]}
                                    register={register}
                                    errors={errors}
                                />
                                <RHFInputBox label="Volume" name="volume" register={register} type="text" errors={errors} />
                                <RHFInputBox label="Page" name="page" register={register} type="text" errors={errors} />
                                <RHFInputBox label="Inst. Number" name="instrumentNumber" register={register} type="text" errors={errors} />
                                <RHFInputBox
                                    label="Record Date"
                                    name="recordDate"
                                    register={register}
                                    type="date"
                                    errors={errors}
                                />
                            </>
                        )}
                        <RHFToggle label="Filed by Memorandum" name="isFiledByMemo" register={register} errors={errors} disabled={isReadonly} />
                    </div>
                    <div className="form-row">
                        <RHFInputBox
                            label="Original Lessor"
                            name="originalLessor"
                            register={register}
                            type="text"
                            errors={errors}
                            isRequired={true}
                            disabled={isReadonly}
                        />
                        <RHFInputBox
                            label="Original Lessee"
                            name="originalLessee"
                            register={register}
                            type="text"
                            errors={errors}
                            disabled={isReadonly}
                        />
                        <RHFInputBox label="Lessor AKA" name="lessorAka" register={register} type="text" errors={errors} disabled={isReadonly} />

                        <RHFInputBox label="Leased By" name="leasedBy" register={register} type="text" errors={errors} disabled={isReadonly} />
                        <RHFInputBox label="Client Doc Number" name="clientDocNumber" register={register} type="text" errors={errors} />
                    </div>
                    <RHFLegend title="Right Of Way Hold" />
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                        <div className="form-row">
                            <Button text="Previous" onClick={previousContractHold} size="small" intent="primary" />
                            {/* <span onClick={previousLeasehold} style={{ border: '1px solid black' }} className="clickable">Previous</span> */}
                            <span style={{ padding: '.5rem' }}>{`${currentLeaseHold + 1} of ${rightOfWayData?.rightOfWayHolds?.length || 1}`}</span>
                            <Button text="Next" onClick={nextContractHold} size="small" intent="primary" />
                            {/* <span onClick={nextLeasehold} style={{ border: '1px solid black' }} className="clickable">Next</span> */}
                            {rightOfWayData?.rightOfWayHolds?.[0]?.surfaceOwner && (
                                <div style={{ marginLeft: '2rem', fontSize: '1.5rem' }}>
                                    <span>Owner: </span>
                                    <span>{OwnerUtil.MapOwner(rightOfWayData?.rightOfWayHolds?.[0]?.surfaceOwner)?.ownerName() || 'N/A'}</span>
                                </div>
                            )}
                        </div>
                        {rightOfWayData?.rightOfWayHolds?.length > 1 && (
                            <div style={{ display: 'flex' }}>
                                {copyLeaseholdClicked && (
                                    <div style={{ textAlign: 'center', marginRight: '1rem' }}>
                                        <RHFSelect
                                            onChange={(e) => handleSourceSelected(e)}
                                            name="sourceLeaseholdId"
                                            label="Source Leasehold"
                                            options={leaseholdList()}
                                            register={register}
                                        />
                                        <Button
                                            style={{ marginTop: '1rem' }}
                                            disabled={!sourceSelected}
                                            text="Confirm Copy"
                                            intent="danger"
                                            onClick={onCopyLeasehold}
                                        />
                                    </div>
                                )}
                                <div>
                                    {appContext.rowholdId === 0 && (
                                        <Button
                                            style={{ marginLeft: '2rem' }}
                                            text="Copy Leasehold"
                                            onClick={() => setCopyLeaseholdClicked(true)}
                                            intent="primary"
                                        />
                                    )}
                                    <Button
                                        style={{ marginLeft: '2rem' }}
                                        text="Delete Leasehold"
                                        onClick={() => deleteRightOfWayHold(false)}
                                        intent="danger"
                                    />
                                </div>
                            </div>
                        )}
                        {rightOfWayData?.rightOfWayHolds?.length === 1 && (
                            <div style={{ display: 'flex' }}>
                                <div>
                                    <Button
                                        style={{ marginLeft: '2rem' }}
                                        text="Delete Leasehold"
                                        onClick={() => deleteRightOfWayHold(true)}
                                        intent="danger"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="form-row">
                        <RHFCheckbox
                            style={{ marginBottom: '2rem' }}
                            label={'Active Lease'}
                            name={'currentHold.isActive'}
                            register={register}
                            errors={errors}
                        />
                    </div>
                    <div hidden={true}>
                        <RHFInputBox label="" name="currentHold.id" register={register} type="number" disabled={true} errors={errors} />
                        <RHFInputBox label="" name="currentHold.rightOfWayId" register={register} type="number" disabled={true} errors={errors} />
                        <RHFInputBox label="" name="currentHold.projectId" register={register} type="number" disabled={true} errors={errors} />
                        <RHFInputBox label="" name="currentHold.rowHoldNumber" register={register} disabled={true} errors={errors} />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Agent Id"
                            name="currentHold.agentId"
                            register={register}
                            errors={errors}
                            bodyStyle={{ width: '15%' }}
                            style={{ width: '100%' }}
                            disabled={isReadonly}
                        />
                        {handleVisibility('noLines') && (
                            <RHFInputBox
                                label="No. Lines"
                                name="numberOfLines"
                                register={register}
                                type="number"
                                errors={errors}
                                bodyStyle={{ width: '15%' }}
                                style={{ width: '100%' }}
                                disabled={isReadonly}
                            />
                        )}
                    </div>

                    <div className="form-row">
                        {handleVisibility('rowLength') && (
                            <>
                                <RHFInputBox
                                    label="ROW Length"
                                    name="rowLength"
                                    register={register}
                                    type="number"
                                    errors={errors}
                                    disabled={isReadonly}
                                    bodyStyle={{ width: '15%' }}
                                    style={{ width: '100%' }}
                                />
                                <RHFSelect
                                    label=" "
                                    name="rowLengthTypeId"
                                    options={AgreementUtil.getOptions(rowLengths, 'Length')}
                                    register={register}
                                    errors={errors}
                                    disabled={isReadonly}
                                    bodyStyle={{ width: '15%' }}
                                    style={{ width: '100%' }}
                                />
                            </>
                        )}
                        {handleVisibility('rowWidth') && (
                            <>
                                <RHFInputBox
                                    label="ROW Width"
                                    name="rowWidth"
                                    register={register}
                                    type="number"
                                    errors={errors}
                                    disabled={isReadonly}
                                    bodyStyle={{ width: '15%' }}
                                    style={{ width: '100%' }}
                                />
                                <RHFSelect
                                    label=" "
                                    name="rowWidthTypeId"
                                    options={AgreementUtil.getOptions(rowLengths, 'Length')}
                                    register={register}
                                    errors={errors}
                                    disabled={isReadonly}
                                    bodyStyle={{ width: '15%' }}
                                    style={{ width: '100%' }}
                                />
                            </>
                        )}
                        {handleVisibility('tempWidth') && (
                            <>
                                <RHFInputBox
                                    label="Temp Width"
                                    name="tempWidth"
                                    register={register}
                                    type="number"
                                    errors={errors}
                                    disabled={isReadonly}
                                    bodyStyle={{ width: '15%' }}
                                    style={{ width: '100%' }}
                                />
                                <RHFSelect
                                    label=" "
                                    name="tempWidthTypeId"
                                    options={AgreementUtil.getOptions(rowLengths, 'Length')}
                                    register={register}
                                    errors={errors}
                                    disabled={isReadonly}
                                    bodyStyle={{ width: '15%' }}
                                    style={{ width: '100%' }}
                                />
                            </>
                        )}
                    </div>
                    {handleVisibility('pipeDiameter') && (
                        <div className="form-row">
                            <RHFInputBox
                                label="Pipeline Size"
                                name="pipeLineSize"
                                register={register}
                                type="number"
                                errors={errors}
                                disabled={isReadonly}
                                bodyStyle={{ width: '15%' }}
                                style={{ width: '100%' }}
                            />
                            <RHFSelect
                                label=" "
                                name="pipeLineSizeTypeId"
                                options={AgreementUtil.getOptions(diameterLengths, 'Length')}
                                register={register}
                                errors={errors}
                                disabled={isReadonly}
                                bodyStyle={{ width: '15%' }}
                                style={{ width: '100%' }}
                            />
                            <RHFInputBox
                                label="Not Exceeding"
                                name="pipeLineNotExceeding"
                                register={register}
                                type="number"
                                errors={errors}
                                disabled={isReadonly}
                                bodyStyle={{ width: '15%' }}
                                style={{ width: '100%' }}
                            />
                            <RHFSelect
                                label=" "
                                name="notExceedingTypeId"
                                options={AgreementUtil.getOptions(diameterLengths, 'Length')}
                                register={register}
                                errors={errors}
                                disabled={isReadonly}
                                bodyStyle={{ width: '15%' }}
                                style={{ width: '100%' }}
                            />
                        </div>
                    )}
                    <div className="form-row">
                        {handleVisibility('consideration') && (
                            <>
                                <RHFInputBox
                                    label="Consideration"
                                    name="consideration"
                                    register={register}
                                    type="number"
                                    errors={errors}
                                    disabled={isReadonly}
                                    bodyStyle={{ width: '15%' }}
                                    style={{ width: '100%' }}
                                    onChange={() => handleTotals(getValues() as unknown as RightOfWay)}
                                />
                                <RHFSelect
                                    label=" "
                                    name="considerationType"
                                    options={ROW_PAYMENT_OPTIONS}
                                    register={register}
                                    errors={errors}
                                    disabled={isReadonly}
                                    bodyStyle={{ width: '15%' }}
                                    style={{ width: '100%' }}
                                    onChange={() => handleTotals(getValues() as unknown as RightOfWay)}
                                />
                            </>
                        )}
                        {handleVisibility('extConsideration') && (
                            <>
                                <RHFInputBox
                                    label="Ext. Consideration"
                                    name="extraConsideration"
                                    register={register}
                                    type="number"
                                    errors={errors}
                                    disabled={isReadonly}
                                    bodyStyle={{ width: '15%' }}
                                    style={{ width: '100%' }}
                                    onChange={() => handleTotals(getValues() as unknown as RightOfWay)}
                                />
                                <RHFSelect
                                    label=" "
                                    name="extraConsiderationType"
                                    options={ROW_PAYMENT_OPTIONS}
                                    register={register}
                                    errors={errors}
                                    disabled={isReadonly}
                                    bodyStyle={{ width: '15%' }}
                                    style={{ width: '100%' }}
                                    onChange={() => handleTotals(getValues() as unknown as RightOfWay)}
                                />
                            </>
                        )}
                        {handleVisibility('damages') && (
                            <>
                                <RHFInputBox
                                    label="Damages"
                                    name="damages"
                                    register={register}
                                    type="number"
                                    errors={errors}
                                    disabled={isReadonly}
                                    bodyStyle={{ width: '15%' }}
                                    style={{ width: '100%' }}
                                    onChange={() => handleTotals(getValues() as unknown as RightOfWay)}
                                />
                                <RHFSelect
                                    label=" "
                                    name="damagesType"
                                    options={ROW_PAYMENT_OPTIONS}
                                    register={register}
                                    errors={errors}
                                    disabled={isReadonly}
                                    bodyStyle={{ width: '15%' }}
                                    style={{ width: '100%' }}
                                    onChange={() => handleTotals(getValues() as unknown as RightOfWay)}
                                />
                                <RHFToggle 
                                    name="damagesPaymentUpFront" 
                                    label="Payment Up Front?" 
                                    register={register} 
                                    bodyStyle={{ width: '15%' }}
                                    style={{ width: '100%' }}
                                    errors={errors} 
                                />
                            </>
                        )}
                        {handleVisibility('payTermOrder') && (
                            <>
                                <RHFInputBox
                                    label="Pay Term Order"
                                    name="payTermOrder"
                                    register={register}
                                    type="number"
                                    errors={errors}
                                    disabled={isReadonly}
                                    bodyStyle={{ width: '15%' }}
                                    style={{ width: '100%' }}
                                />
                                <RHFSelect
                                    label=" "
                                    name="payTermTypeId"
                                    options={AgreementUtil.getOptions(measurementTypes, 'Time')}
                                    register={register}
                                    errors={errors}
                                    disabled={isReadonly}
                                    bodyStyle={{ width: '15%' }}
                                    style={{ width: '100%' }}
                                    onChange={() => handleTotals(getValues() as unknown as RightOfWay)}
                                />
                            </>
                        )}
                    </div>
                    {handleVisibility('rental') && (
                        <div className="form-row">
                            <RHFToggle 
                                name="isRental" 
                                label="Rental Agreement?" 
                                register={register} 
                                style={{ width: '100%' }}
                                errors={errors} 
                                onChange={(e) => checkRental(e)}
                            />
                            {isRental && (
                                <>
                                    <RHFInputBox
                                        label="Rental Amount"
                                        name="rentalAmount"
                                        register={register}
                                        type="number"
                                        errors={errors}
                                        disabled={isReadonly}
                                        style={{ width: '100%' }}
                                    />
                                    <RHFSelect
                                        label=" "
                                        name="rentalAmountType"
                                        options={ROW_PAYMENT_OPTIONS}
                                        register={register}
                                        errors={errors}
                                        disabled={isReadonly}
                                        style={{ width: '100%' }}
                                        onChange={() => handleTotals(getValues() as unknown as RightOfWay)}
                                    />
                                    <RHFInputBox
                                        label="Escalating Percentage"
                                        name="rentalEscalatingPercentage"
                                        register={register}
                                        type="number"
                                        errors={errors}
                                        disabled={isReadonly}
                                        style={{ width: '100%' }}
                                    />
                                </>
                            )}
                        </div>
                    )}
                    <div className="form-row">
                        <RHFToggle 
                            name="isPerpetual" 
                            label="Is Perpetual?" 
                            bodyStyle={{ width: '15%' }} 
                            register={register} 
                            errors={errors}
                            onChange={(e) => checkPerpetual(e)}
                        />
                    </div>
                    <div className={isPerpetual ? '' : 'form-row'} hidden={isPerpetual}>
                        <RHFInputBox
                            label="Primary Term"
                            name="currentHold.primaryTerm"
                            register={register}
                            type="number"
                            errors={errors}
                            disabled={isReadonly}
                            bodyStyle={{ width: '25%' }}
                            style={{ width: '100%' }}
                        />
                        <RHFSelect
                            label=" "
                            name="currentHold.primaryTermTypeId"
                            options={AgreementUtil.getOptions(measurementTypes, 'Time')}
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                            bodyStyle={{ width: '25%' }}
                            style={{ width: '100%' }}
                        />
                        <RHFInputBox
                            label="Extension Term"
                            name="currentHold.extensionTerm"
                            register={register}
                            type="number"
                            errors={errors}
                            disabled={isReadonly}
                            bodyStyle={{ width: '25%' }}
                            style={{ width: '100%' }}
                        />
                        <RHFSelect
                            label=" "
                            name="currentHold.extensionTermTypeId"
                            options={AgreementUtil.getOptions(measurementTypes, 'Time')}
                            register={register}
                            errors={errors}
                            disabled={isReadonly}
                            bodyStyle={{ width: '20%' }}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className="form-row">
                        {handleVisibility('royalty') && (
                            <>
                                <RHFInputBox
                                    label="Royalty"
                                    name="currentHold.royalty"
                                    register={register}
                                    type="number"
                                    errors={errors}
                                    disabled={isReadonly}
                                />
                            </>
                        )}
                    </div>

                    <RHFLegend title="Original Legal Description" />
                    <div className="form-row">
                        <RHFTextArea
                            label={''}
                            name="legalDescription"
                            style={{
                                width: '100%',
                                height: '6rem',
                                border: '0',
                                borderBottom: '1px solid black',
                                resize: 'none',
                                overflowY: 'auto',
                            }}
                            register={register}
                            disabled={isReadonly}
                        />
                    </div>

                    {handleVisibility('addlDamages') && (
                        <>
                            <RHFLegend title="Additional Damages" />

                            <div style={{ margin: '2rem' }}>
                                <AdditionalDamagesList
                                    additionalDamages={additionalDamages}
                                    measurementTypes={measurementTypes}
                                    namePrefix="currentHold.additionalDamages"
                                    netLength={getValues()?.rowLength ? parseFloat(getValues().rowLength) : 0}
                                    calculateTotals={true}
                                    costApplied={COST_APPLIED}
                                    handleTotals={() => handleTotals(getValues() as unknown as RightOfWay)}
                                    calcRowTotal={calcRowTotal}
                                    handleRemoveDamages={handleRemoveDamages}
                                    rowPayOptions={ROW_PAYMENT_OPTIONS}
                                    onAddDamages={onAddDamages}
                                    getValues={getValues}
                                    setValue={setValue}
                                    register={register}
                                    errors={errors}
                                />
                            </div>
                        </>
                    )}

                    <br />

                    <RHFLegend title="Total Considerations" />

                    <FormRow style={{ margin: '2rem' }}>
                        <label className="form-label mr-2">
                            <b>Total Consideration: </b>
                            <span className="readonly-field">{considerationTotal}</span>
                        </label>
                        <label className="form-label mr-2">
                            <b>Initial Damages: </b>
                            <span className="readonly-field">{initialDamages}</span>
                        </label>
                        <label className="form-label mr-2">
                            <b>Up Front Damages: </b>
                            <span className="readonly-field">{upFrontDamages}</span>
                        </label>
                        <label className="form-label mr-2">
                            <b>Balance Damages: </b>
                            <span className="readonly-field">{balanceDamages}</span>
                        </label>
                        <label className="form-label mr-2">
                            <b>Total Costs: </b>
                            <span className="readonly-field">{totalCosts}</span>
                        </label>
                    </FormRow>

                    <RHFLegend title="Restrictions" />

                    <div style={{ margin: '2rem' }}>
                        <RestrictionList
                            restrictions={restrictions}
                            namePrefix="currentHold.restrictions"
                            handleRemoveRestrictions={handleRemoveRestrictions}
                            onAddRestriction={onAddRestrictions}
                            getValues={getValues}
                            setValue={setValue}
                            register={register}
                            errors={errors}
                        />
                    </div>

                    {attachedProvisions !== undefined && (
                        <>
                            <RHFLegend title="Provisions" />
                            <div style={{ margin: '2rem' }}>
                                <ProvisionClause
                                    attachedProvisionClauses={attachedProvisions}
                                    options={provisionClauseTypes}
                                    formPrefix="currentHold.provisionLeaseClauses"
                                    register={register}
                                    errors={errors}
                                    getValues={getValues}
                                    setValue={setValue}
                                    key="provisionClause"
                                />
                            </div>
                        </>
                    )}

                    <br />

                    <RHFLegend title="Document Management" />
                    {rightOfWayData?.rightOfWayHolds && (
                        <ContractHoldDocs
                            showDialog={(dialogName: string) => dispatch(actions.layout.showDialog(dialogName))}
                            hideDialog={() => dispatch(actions.layout.hideDialog())}
                            contractLookupId={rightOfWayData?.rightOfWayHolds?.[currentLeaseHold]?.contractLookupId}
                            contractType={'Contract Hold'}
                        />
                    )}

                    <div style={{ minHeight: '20rem', marginTop: '2rem' }}>
                        <RHFLegend title="Contract Ownership" />
                        <span style={warningStyle}>ALL INTEREST REPRESENTED AS DECIMAL</span>
                        <ContractWorkingInterestList
                            isGovStateBlm={false}
                            owners={rightOfWayData?.rightOfWayHolds?.[currentLeaseHold]?.wiOwners}
                            wiContractOwnerships={rightOfWayData?.rightOfWayHolds?.[currentLeaseHold]?.contractOwnerships}
                            prefix={'currentHold'}
                            setDialogShowing={(dialogName: string) => dispatch(actions.layout.showDialog(dialogName))}
                            tryAddWI={onTryAddWI}
                            onAddORRI={onAddORRI}
                            onCopyORRI={onCopyORRI}
                            onDeleteORRI={onDeleteORRI}
                            deleteContractOwnership={onDeleteContractOwnership}
                            openOwnershipNotes={openOwnershipNotes}
                            register={register}
                            errors={errors}
                            getValues={getValues}
                            setValue={setValue}
                            watch={watch}
                            royalty={0}
                        />
                    </div>

                    <div style={{ marginTop: '5rem' }}>
                        <Button text="Save" type="submit" intent="primary" />
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RightOfWayInfo;



// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/LeaseholdInfoComponents/RightOfWayInfo.tsx